export const CHECKOUT_SUBMIT_PHASE = {
  IDLE: 'idle',
  LT_PLUS: 'ltPlus',
  CONFIRMING_MEMBERSHIP: 'confirmingMembership',
  REFRESHING_BOOKING: 'refreshingBooking',
  CONFIRMING_PAYMENT: 'confirmingPayment',
  SUBMITTING_ORDER: 'submittingOrder',
};

const LT_PLUS_DISPLAY_STEP_KEYS = {
  creatingMembership: 'creatingMembership',
  confirmingMembership: 'confirmingMembership',
  confirmingPayment: 'confirmingPayment',
  submittingOrder: 'submittingOrder',
};

const STANDARD_DISPLAY_STEP_KEYS = {
  confirmingPayment: 'confirmingPayment',
  submittingOrder: 'submittingOrder',
};

const mapPhaseToDisplayStep = (phase, includeLtPlus) => {
  if (!includeLtPlus) {
    return phase;
  }

  // Creating the LandTrust+ subscription (server call that returns the client secret).
  if (phase === CHECKOUT_SUBMIT_PHASE.LT_PLUS) {
    return LT_PLUS_DISPLAY_STEP_KEYS.creatingMembership;
  }

  // Confirming/charging the membership payment intent (Stripe confirmPayment).
  if (phase === CHECKOUT_SUBMIT_PHASE.CONFIRMING_MEMBERSHIP) {
    return LT_PLUS_DISPLAY_STEP_KEYS.confirmingMembership;
  }

  // The post-membership booking re-price prepares the booking payment, so it advances
  // the modal to the payment step instead of leaving it on the membership steps.
  if (
    phase === CHECKOUT_SUBMIT_PHASE.REFRESHING_BOOKING ||
    phase === CHECKOUT_SUBMIT_PHASE.CONFIRMING_PAYMENT
  ) {
    return LT_PLUS_DISPLAY_STEP_KEYS.confirmingPayment;
  }

  if (phase === CHECKOUT_SUBMIT_PHASE.SUBMITTING_ORDER) {
    return LT_PLUS_DISPLAY_STEP_KEYS.submittingOrder;
  }

  return null;
};

const getDisplayStepOrder = includeLtPlus =>
  includeLtPlus
    ? [
        LT_PLUS_DISPLAY_STEP_KEYS.creatingMembership,
        LT_PLUS_DISPLAY_STEP_KEYS.confirmingMembership,
        LT_PLUS_DISPLAY_STEP_KEYS.confirmingPayment,
        LT_PLUS_DISPLAY_STEP_KEYS.submittingOrder,
      ]
    : [STANDARD_DISPLAY_STEP_KEYS.confirmingPayment, STANDARD_DISPLAY_STEP_KEYS.submittingOrder];

export const buildCheckoutSubmitSteps = ({ phase, stepLabels, includeLtPlus }) => {
  const displayStepOrder = getDisplayStepOrder(includeLtPlus);
  const activeDisplayStep = mapPhaseToDisplayStep(phase, includeLtPlus);
  const currentIndex = displayStepOrder.indexOf(activeDisplayStep);

  const stepDefinitions = includeLtPlus
    ? [
        {
          key: LT_PLUS_DISPLAY_STEP_KEYS.creatingMembership,
          text: stepLabels.creatingMembership,
        },
        {
          key: LT_PLUS_DISPLAY_STEP_KEYS.confirmingMembership,
          text: stepLabels.confirmingMembership,
        },
        { key: LT_PLUS_DISPLAY_STEP_KEYS.confirmingPayment, text: stepLabels.confirmingPayment },
        { key: LT_PLUS_DISPLAY_STEP_KEYS.submittingOrder, text: stepLabels.submittingOrder },
      ]
    : [
        { key: STANDARD_DISPLAY_STEP_KEYS.confirmingPayment, text: stepLabels.confirmingPayment },
        { key: STANDARD_DISPLAY_STEP_KEYS.submittingOrder, text: stepLabels.submittingOrder },
      ];

  return stepDefinitions.map(step => {
    const stepIndex = displayStepOrder.indexOf(step.key);

    if (currentIndex === -1 || stepIndex < currentIndex) {
      return { text: step.text, status: 'completed' };
    }

    if (stepIndex === currentIndex) {
      return { text: step.text, status: 'active' };
    }

    return { text: step.text, status: 'pending' };
  });
};

export const isCheckoutSubmitInProgress = phase => phase !== CHECKOUT_SUBMIT_PHASE.IDLE;
